import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Chip,
  Typography,
  IconButton,
  Stack,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { EditableFieldKey } from 'src/components/pages/InvoiceGroups/InvoiceGroupManager';

interface SplitFieldModalProps {
  open: boolean;
  fieldKey: EditableFieldKey;
  groupId: number;
  options: string[];
  initialValue: string;
  onUpdate: (updatedValue: string) => void;
  onClose: () => void;
}

export const splitOptions: Record<EditableFieldKey, string[]> = {
  splitInvoicesBy: [
    'accounting_code',
    'base_subscription_id',
    'billing_type_id',
    'building_id',
    'city_id',
    'client_id',
    'invoice_item',
    'ownership_group_id',
    'partner_id',
    'po_number',
    'sales_rep_id',
    'service_id',
    'subscription_id',
  ],
  groupLinesBy: [
    'accounting_code',
    'base_subscription_id',
    'billing_frequency_id',
    'billing_type_id',
    'building_id',
    'city_id',
    'client_id',
    'partner_client_id',
    'invoice_item',
    'ownership_group_id',
    'partner_id',
    'po_number',
    'sales_rep_id',
    'service_id',
    'subscription_id',
  ],
  lineItemFields: ['invoice_item'],
  lineDescriptionFields: [
    'building_name',
    'accounting_code',
    'address',
    'city_name',
    'client_name',
    'invoice_description',
    'ownership_group_name',
  ],
};

const SplitFieldModal: React.FC<SplitFieldModalProps> = ({
  open,
  fieldKey,
  groupId,
  options,
  initialValue,
  onUpdate,
  onClose,
}) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelected(new Set(initialValue.split(',').filter(Boolean)));
  }, [initialValue]);

  const toggleOption = (option: string) => {
    const next = new Set(selected);
    if (next.has(option)) {
      next.delete(option);
    } else {
      next.add(option);
    }
    setSelected(next);
    onUpdate(Array.from(next).join(','));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Grid container justifyContent="space-between">
          <Grid item xs={11} >
            <Typography variant="h6">Edit {fieldKey} - Group #{groupId}</Typography>
          </Grid>
          <Grid item xs={1}>
            <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" mb={1}>
          Click to add/remove options:
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {options.map((option) => {
            const isSelected = selected.has(option);
            return (
              <Chip
                key={option}
                label={option}
                onClick={() => toggleOption(option)}
                color={isSelected ? 'success' : 'default'}
                variant={isSelected ? 'filled' : 'outlined'}
                clickable
              />
            );
          })}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SplitFieldModal;
